// Hooks TanStack Query para notificacoes.
//
// Convencao de keys (paralelo a transactions/budgets):
//   ['notifications', 'list', filters]
//   ['notifications', 'unread-count']
//   ['notifications', 'preferences']
//
// Mutations invalidam `['notifications']` (prefix) — atinge lista + count.
// Update de preferences invalida apenas o ramo `preferences`.
//
// Bell badge usa `refetchInterval = 60s` — polling periodico para atualizar
// contagem mesmo sem mark/create local. SSE/WebSocket fica para fase futura.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  NotificationListFilters,
  UpdatePreferencesRequest,
} from '../types/notification.types';

import { notificationsApi } from './notifications.api';

import { logger } from '@/shared/lib/logger';

export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (filters: NotificationListFilters) => [...notificationKeys.lists(), filters] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
  preferences: () => [...notificationKeys.all, 'preferences'] as const,
};

export function useNotificationsQuery(filters: NotificationListFilters = {}) {
  return useQuery({
    queryKey: notificationKeys.list(filters),
    queryFn: () => notificationsApi.list(filters),
    placeholderData: keepPreviousData,
  });
}

/**
 * Count de nao-lidas para o bell badge. Fetch lightweight: pede 1 item e le
 * `meta.total` da resposta. Polling 60s para atualizar sem websockets.
 */
export function useUnreadNotificationCountQuery() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () => notificationsApi.list({ is_read: false, page: 1, per_page: 1 }),
    select: (response) => response.meta.total,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
  });
}

export function useNotificationPreferencesQuery() {
  return useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: () => notificationsApi.getPreferences(),
    select: (response) => response.data,
  });
}

export function useMarkAsReadMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.markAsRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
    },
    onError: (error) => {
      logger.app.error('Falha ao marcar notificacao como lida:', error);
    },
  });
}

export function useMarkAllAsReadMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => notificationsApi.markAllAsRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
    },
    onError: (error) => {
      logger.app.error('Falha ao marcar todas as notificacoes como lidas:', error);
    },
  });
}

export function useUpdateNotificationPreferencesMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdatePreferencesRequest) => notificationsApi.updatePreferences(body),
    onSuccess: (response) => {
      // Atualiza o cache imediatamente com a resposta autoritativa do
      // backend — toggle reflete o novo estado sem esperar pelo refetch
      // do invalidate. Em caso de erro, ainda invalidamos para garantir
      // que a UI volte ao estado de verdade do servidor.
      queryClient.setQueryData(notificationKeys.preferences(), response);
      void queryClient.invalidateQueries({ queryKey: notificationKeys.preferences() });
    },
    onError: (error) => {
      logger.app.error('Falha ao atualizar preferencias de notificacao:', error);
    },
  });
}
